import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import {
  Phone, PhoneCall, PhoneOff, Clock, Download, Plus,
  X, ArrowUpRight, ArrowDownLeft, Mic
} from 'lucide-react';

export default function CallHistory() {
  const { user } = useAuth();
  const isVoip = user?.agent_type === 'voip_agent' || user?.role === 'admin' || user?.role === 'executive';
  const [calls, setCalls] = useState({ voip_calls: [], manual_calls: [] });
  const [loading, setLoading] = useState(true);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({ notes: '', duration_minutes: 0, lead_id: '' });
  const [callNumber, setCallNumber] = useState('');
  const [showDialer, setShowDialer] = useState(false);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      const res = await api.get('/api/calls');
      setCalls(res.data);
    } catch (err) {
      console.error('Error loading calls:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOutboundCall = async () => {
    if (!callNumber) return;
    try {
      const res = await api.post('/api/calls/outbound', { phone_number: callNumber });
      alert(`Call initiated! SID: ${res.data.call_sid}`);
      setCallNumber('');
      setShowDialer(false);
      loadCalls();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to initiate call');
    }
  };

  const handleManualLog = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/calls/manual', manualForm);
      setShowManualForm(false);
      setManualForm({ notes: '', duration_minutes: 0, lead_id: '' });
      loadCalls();
    } catch (err) {
      alert('Error logging call');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-white">Call History</h3>
          <p className="text-dark-400 text-sm">{calls.total_voip} VoIP calls, {calls.total_manual} manual calls</p>
        </div>
        <div className="flex gap-2">
          {isVoip && (
            <button
              onClick={() => setShowDialer(!showDialer)}
              className="flex items-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors text-sm"
            >
              <PhoneCall className="w-4 h-4" />
              Click to Call
            </button>
          )}
          {user?.role === 'normal_agent' && (
            <button
              onClick={() => setShowManualForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 font-medium rounded-lg transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Log Call
            </button>
          )}
        </div>
      </div>

      {/* Dialer */}
      {showDialer && (
        <div className="glass rounded-xl p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="tel"
              value={callNumber}
              onChange={(e) => setCallNumber(e.target.value)}
              placeholder="+919876543210"
              className="flex-1 px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-500 text-sm focus:outline-none focus:border-green-500"
            />
            <button
              onClick={handleOutboundCall}
              disabled={!callNumber}
              className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors text-sm disabled:opacity-50"
            >
              <PhoneCall className="w-4 h-4 inline mr-2" />
              Call Now
            </button>
            <button
              onClick={() => setShowDialer(false)}
              className="px-4 py-2.5 bg-dark-800 text-dark-300 hover:text-white rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* VoIP Calls */}
      {(isVoip || user?.role === 'admin') && (
        <div className="glass rounded-xl overflow-hidden">
          <div className="p-4 border-b border-dark-700">
            <h4 className="text-white font-medium flex items-center gap-2">
              <Phone className="w-4 h-4 text-green-400" />
              VoIP Calls
            </h4>
          </div>
          {calls.voip_calls.length === 0 ? (
            <div className="p-8 text-center text-dark-400">
              <PhoneOff className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No VoIP calls yet</p>
            </div>
          ) : (
            <div className="divide-y divide-dark-800">
              {calls.voip_calls.map((call) => (
                <div key={call.id} className="p-4 hover:bg-dark-800/30 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        call.direction === 'outbound' ? 'bg-blue-500/20' : 'bg-green-500/20'
                      }`}>
                        {call.direction === 'outbound' ? (
                          <ArrowUpRight className="w-4 h-4 text-blue-400" />
                        ) : (
                          <ArrowDownLeft className="w-4 h-4 text-green-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">
                          {call.direction === 'inbound' ? call.from_number : call.to_number}
                          {call.direction === 'inbound' && call.routed_to && (
                            <span className="block text-xs text-dark-500 mt-0.5">
                              → {call.routed_to.includes('all_executives') ? 'rang all executives' : `routed to ${call.routed_to}`}
                            </span>
                          )}
                        </p>
                        <p className="text-dark-400 text-xs">
                          {call.agent_name && <span className="text-primary-400">{call.agent_name} · </span>}{call.direction} via {call.twilio_number_used} &bull; {new Date(call.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => {
                        const num = call.direction === 'inbound' ? call.from_number : call.to_number;
                        if (!num || num === 'all_executives') return;
                        if (user?.agent_type === 'normal_agent') { window.open('tel:'+num,'_self'); return; }
                        api.post('/api/calls/outbound', { phone_number: num, agent_id: user.id })
                          .then(r => alert(r.data.message || 'Calling...'))
                          .catch(() => alert('Error making call'));
                      }} className="p-1.5 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded" title="Call back">
                        <PhoneCall className="w-3.5 h-3.5" />
                      </button>
                      {call.recording_url && (
                        <a href={call.recording_url ? `/api/calls/recording/${call.recording_url.split('/Recordings/')[1]?.replace('.mp3','')}` : '#'} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs hover:bg-purple-500/20">
                          <Mic className="w-3 h-3" /> Recording
                        </a>
                      )}
                      <span className="flex items-center gap-1 text-dark-400 text-xs">
                        <Clock className="w-3 h-3" />
                        {formatDuration(call.duration)}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs ${
                        call.status === 'completed' ? 'bg-green-500/10 text-green-400' :
                        call.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                        'bg-yellow-500/10 text-yellow-400'
                      }`}>{call.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manual Calls */}
      {calls.manual_calls.length > 0 && (
        <div className="glass rounded-xl overflow-hidden">
          <div className="p-4 border-b border-dark-700">
            <h4 className="text-white font-medium flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-blue-400" />
              Manual Call Logs
            </h4>
          </div>
          <div className="divide-y divide-dark-800">
            {calls.manual_calls.map((call) => (
              <div key={call.id} className="p-4 hover:bg-dark-800/30 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white text-sm">{call.notes || 'No notes'}</p>
                    <p className="text-dark-400 text-xs mt-1">
                      Duration: {call.duration_minutes}m &bull; {new Date(call.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Log Form */}
      {showManualForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white">Log Manual Call</h3>
              <button onClick={() => setShowManualForm(false)} className="p-1 text-dark-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleManualLog} className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-dark-300 mb-1.5">Duration (minutes)</label>
                <input
                  type="number"
                  value={manualForm.duration_minutes}
                  onChange={(e) => setManualForm({...manualForm, duration_minutes: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm text-dark-300 mb-1.5">Notes</label>
                <textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm({...manualForm, notes: e.target.value})}
                  className="w-full px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white text-sm focus:outline-none focus:border-primary-500 resize-none h-24"
                  placeholder="Call details..."
                />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowManualForm(false)}
                  className="flex-1 py-2.5 bg-dark-800 text-dark-300 hover:text-white rounded-lg text-sm font-medium">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-dark-900 rounded-lg text-sm font-medium">
                  Save Call Log
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
